<section class="utilization-page">
  <header class="page-header">
    <div>
      <h1 class="page-title">Utilization</h1>
      <p class="page-sub">Understand how EDC is being used to create work orders and how reports are being consumed to drive action.</p>
    </div>
    <div class="header-actions">
      <button type="button" class="header-btn">
        <i class="pi pi-calendar"></i>
        <span>May 12 - Jun 10, 2025</span>
        <i class="pi pi-angle-down"></i>
      </button>
      <button type="button" class="header-btn">
        <i class="pi pi-filter"></i>
        <span>Filters</span>
      </button>
    </div>
  </header>

  <div class="kpi-grid">
    <article class="kpi-card" *ngFor="let item of kpiCards; let i = index; trackBy: trackByIndex">
      <div class="kpi-top">
        <span class="kpi-icon" [class]="kpiCardMeta[i].iconClass">
          <i class="pi" [class]="kpiCardMeta[i].icon"></i>
        </span>
        <span class="kpi-label">{{ item.label }}</span>
      </div>
      <div class="kpi-value">{{ item.value }}</div>
      <div class="kpi-delta" [class.up]="item.trend === 'up'">
        {{ item.delta }}
      </div>
      <div class="kpi-note" *ngIf="i < 2 || i === 4">vs previous 30 days</div>
      <div class="kpi-note" *ngIf="i === 5">13 min per EDC WO</div>
    </article>
  </div>

  <div class="tab-navigation" role="tablist" aria-label="Utilization tabs">
    <button class="tab-button" [class.active]="activeTab === 'overview'" (click)="selectTab('overview')" role="tab" [attr.aria-selected]="activeTab === 'overview'">
      <i class="pi pi-info-circle"></i>
      <span>Overview</span>
    </button>
    <button class="tab-button" [class.active]="activeTab === 'edcUsage'" (click)="selectTab('edcUsage')" role="tab" [attr.aria-selected]="activeTab === 'edcUsage'">
      <i class="pi pi-wrench"></i>
      <span>EDC Usage</span>
    </button>
    <button class="tab-button" [class.active]="activeTab === 'reportConsumption'" (click)="selectTab('reportConsumption')" role="tab" [attr.aria-selected]="activeTab === 'reportConsumption'">
      <i class="pi pi-chart-line"></i>
      <span>Report Consumption</span>
    </button>
  </div>

  <div class="tab-content" *ngIf="activeTab === 'overview'" role="tabpanel">
    <div class="grid-three top-row">
      <article class="panel">
        <div class="panel-head">
          <h3 class="section-title">Utilization Funnel <i class="pi pi-info-circle"></i></h3>
          <p class="muted">How users move from EDC access to report consumption.</p>
        </div>
        <div class="funnel-list">
          <div class="funnel-row" *ngFor="let row of funnelRows; let idx = index; trackBy: trackByIndex">
            <div class="funnel-row-main">
              <div class="funnel-left">
                <span class="funnel-step-icon"><i class="pi pi-user"></i></span>
                <span class="funnel-label">{{ row.label }}</span>
              </div>
              <div class="funnel-bar-wrap">
                <div class="funnel-bar" [style.width.%]="row.bar"></div>
              </div>
              <span class="funnel-value">{{ row.value | number }}</span>
            </div>
            <span class="funnel-conversion" *ngIf="idx > 0">{{ row.conversion }}</span>
          </div>
        </div>
        <div class="funnel-footer">
          <span>Overall Conversion (Access → Monthly)</span>
          <strong>26%</strong>
        </div>
      </article>

      <article class="panel">
        <div class="panel-head">
          <h3 class="section-title">EDC Usage vs. Report Consumption <i class="pi pi-info-circle"></i></h3>
          <p class="muted">Compare operational usage with report engagement.</p>
        </div>
        <div class="scatter-wrap">
          <div class="y-axis-label">Report Views / User</div>
          <svg viewBox="0 0 460 275" preserveAspectRatio="none" aria-label="EDC Usage vs Report Consumption scatter chart">
            <line x1="48" y1="24" x2="48" y2="234" class="axis" />
            <line x1="48" y1="234" x2="436" y2="234" class="axis" />
            <line x1="48" y1="181" x2="436" y2="181" class="grid" />
            <line x1="48" y1="129" x2="436" y2="129" class="grid" />
            <line x1="48" y1="76" x2="436" y2="76" class="grid" />
            <line x1="145" y1="24" x2="145" y2="234" class="grid" />
            <line x1="242" y1="24" x2="242" y2="234" class="grid" />
            <line x1="339" y1="24" x2="339" y2="234" class="grid" />

            <text x="56" y="20" class="quad-text good">High Report Usage</text>
            <text x="268" y="20" class="quad-text good">High EDC Usage</text>
            <text x="56" y="251" class="quad-text risk">Low Report Usage</text>
            <text x="280" y="251" class="quad-text risk">High EDC Usage</text>

            <g *ngFor="let point of scatterRows; trackBy: trackByNamc">
              <circle [attr.cx]="48 + (point.x * 3.88)" [attr.cy]="234 - (point.y * 10.5)" [attr.r]="point.size" [attr.fill]="point.color" fill-opacity="0.95" />
              <text [attr.x]="58 + (point.x * 3.88)" [attr.y]="238 - (point.y * 10.5)" class="point-label">{{ point.namc }}</text>
            </g>

            <text x="230" y="269" class="x-axis-label">EDC Utilization %</text>
            <text x="44" y="236" class="axis-tick">0%</text>
            <text x="139" y="236" class="axis-tick">20%</text>
            <text x="236" y="236" class="axis-tick">40%</text>
            <text x="333" y="236" class="axis-tick">60%</text>
            <text x="425" y="236" class="axis-tick">80%</text>
            <text x="30" y="234" class="axis-tick">0</text>
            <text x="25" y="181" class="axis-tick">5</text>
            <text x="20" y="129" class="axis-tick">10</text>
            <text x="20" y="76" class="axis-tick">15</text>
            <text x="20" y="25" class="axis-tick">20</text>
          </svg>
        </div>
        <p class="scatter-footnote">Bubble size represents number of active users</p>
      </article>

      <article class="panel digital-score-panel">
        <div class="panel-head">
          <h3 class="section-title">Digital Utilization Score <i class="pi pi-info-circle"></i></h3>
        </div>
        <div class="gauge-block">
          <div class="gauge-ring" [style.--score]="gaugeScore">
            <div class="gauge-center">
              <span>{{ gaugeScore }}</span>
              <small>/100</small>
            </div>
          </div>
          <div class="gauge-status">Good</div>
        </div>
        <div class="score-title">Score Breakdown</div>
        <div class="score-breakdown">
          <div class="breakdown-row" *ngFor="let row of scoreBreakdown; trackBy: trackByIndex">
            <span class="weight">{{ row.weight }}%</span>
            <span class="category">{{ row.label }}</span>
            <div class="line-track">
              <div class="line-fill" [style.width.%]="row.value"></div>
            </div>
          </div>
        </div>
        <div class="digital-copy">Combines EDC usage, report consumption, returning user behavior, and data completeness to measure overall digital utilization maturity.</div>
      </article>
    </div>

    <div class="grid-three bottom-row">
      <article class="panel">
        <div class="panel-head table-head">
          <h3 class="section-title">Top 5 NAMCs by Digital Utilization Score</h3>
        </div>
        <div class="ranked-table">
          <div class="table-header">
            <span>NAMC</span>
            <span>Score</span>
            <span>vs Prior 30 Days</span>
            <span>Trend</span>
          </div>
          <div class="table-row" *ngFor="let namc of topNamcs; trackBy: trackByNamc">
            <span class="namc-name">{{ namc.namc }}</span>
            <span class="score">{{ namc.score }}</span>
            <span class="change" [class.up]="namc.trend === 'up'" [class.down]="namc.trend === 'down'">
              <i class="pi" [class.pi-caret-up]="namc.trend === 'up'" [class.pi-caret-down]="namc.trend === 'down'"></i>
              {{ namc.change }}
            </span>
            <span class="trend-line">
              <svg viewBox="0 0 96 20" preserveAspectRatio="none">
                <polyline [attr.points]="namc.sparkline" fill="none" [attr.stroke]="namc.trend === 'up' ? '#28a66a' : '#e25555'" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></polyline>
              </svg>
            </span>
          </div>
        </div>
      </article>

      <article class="panel">
        <div class="panel-head table-head">
          <h3 class="section-title">Lowest Digital Utilization Score</h3>
        </div>
        <div class="ranked-table">
          <div class="table-header">
            <span>NAMC</span>
            <span>Score</span>
            <span>vs Prior 30 Days</span>
            <span>Trend</span>
          </div>
          <div class="table-row" *ngFor="let namc of lowestNamcs; trackBy: trackByNamc">
            <span class="namc-name">{{ namc.namc }}</span>
            <span class="score">{{ namc.score }}</span>
            <span class="change" [class.up]="namc.trend === 'up'" [class.down]="namc.trend === 'down'">
              <i class="pi" [class.pi-caret-up]="namc.trend === 'up'" [class.pi-caret-down]="namc.trend === 'down'"></i>
              {{ namc.change }}
            </span>
            <span class="trend-line">
              <svg viewBox="0 0 96 20" preserveAspectRatio="none">
                <polyline [attr.points]="namc.sparkline" fill="none" [attr.stroke]="namc.trend === 'up' ? '#28a66a' : '#e25555'" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></polyline>
              </svg>
            </span>
          </div>
        </div>
      </article>

      <article class="panel insights-panel">
        <div class="panel-head table-head">
          <h3 class="section-title">Quick Insights</h3>
        </div>
        <div class="insights-list">
          <div class="insight-item" *ngFor="let insight of quickInsights; trackBy: trackByIndex">
            <span class="insight-icon" [class]="insight.iconClass">
              <i class="pi" [class]="insight.icon"></i>
            </span>
            <div class="insight-content">
              <p class="insight-title">{{ insight.title }}</p>
              <p class="insight-detail">{{ insight.detail }}</p>
            </div>
          </div>
        </div>
      </article>
    </div>
  </div>

  <div class="tab-content placeholder" *ngIf="activeTab === 'edcUsage'" role="tabpanel">
    <article class="panel">
      <h3 class="section-title">EDC Usage</h3>
      <p class="muted">Switch back to Overview to see the aligned mockup view.</p>
    </article>
  </div>

  <div class="tab-content placeholder" *ngIf="activeTab === 'reportConsumption'" role="tabpanel">
    <article class="panel">
      <h3 class="section-title">Report Consumption</h3>
      <p class="muted">Switch back to Overview to see the aligned mockup view.</p>
    </article>
  </div>
</section>
